/**
 * Report des règlements d'un document source sur son document aval.
 *
 * ── Le problème ────────────────────────────────────────────────────────────
 * Le statut de règlement n'est PAS une colonne : il se déduit des lignes de
 * `payment_allocations` qui pointent l'id du document (voir
 * computePaymentStatus). Transformer un BL en facture crée un document NEUF,
 * avec un id neuf — les affectations, elles, continuaient de désigner le BL.
 * Un BL encaissé donnait donc une facture « impayée », alors que l'argent
 * était bien rentré.
 *
 * Le relevé client (compute-client-statement) tranche déjà la question : un BL
 * FACTURÉ n'est plus une créance, c'est sa facture qui la porte. L'affectation
 * doit donc suivre le même chemin que la créance et migrer sur la facture.
 *
 * ── La règle ───────────────────────────────────────────────────────────────
 * On reprend au plus ce que la facture RESTE à devoir. Le surplus demeure sur
 * le BL, et ce n'est pas un détail : une facture écarte les articles non
 * facturables (buildDocumentTransform), son total peut donc être inférieur à
 * celui du BL réglé. L'excédent règle des marchandises qui ne sont pas dans la
 * facture — il n'a rien à y faire.
 *
 * À l'inverse, le timbre fiscal (1 DT) n'existe que sur la facture : un BL
 * soldé donne une facture à 1 DT près, donc « partiellement payée ». C'est
 * exact — le client doit bien ce dinar de plus une fois facturé. On ne comble
 * JAMAIS l'écart : le report déplace de l'argent déjà reçu, il n'en invente pas.
 *
 * Purement descriptif : ne lit ni n'écrit rien, l'appelant applique le plan.
 */
import { PAYMENT_EPSILON } from './compute-payment-status';

function round3(n: number): number {
  return Math.round(n * 1000) / 1000;
}

function num(n: unknown): number {
  const v = Number(n);
  return Number.isFinite(v) ? v : 0;
}

/** Ligne `payment_allocations` du document source, réduite à l'utile. */
export interface CarryOverAllocation {
  /** Id de la ligne d'affectation (pas du paiement). */
  id: string;
  paymentId: string;
  /** Document source qui porte l'affectation aujourd'hui. */
  documentId: string;
  allocatedAmount: number;
}

export interface AllocationTransfer {
  allocationId: string;
  paymentId: string;
  sourceDocumentId: string;
  /** Montant repris par le document cible (> 0). */
  movedAmount: number;
  /**
   * Ce qui demeure sur le document source. 0 → la ligne part entière (un
   * simple changement de `document_id`) ; > 0 → la ligne est réduite et une
   * nouvelle ligne naît sur la cible.
   */
  remainingOnSource: number;
}

export interface PaymentCarryOverPlan {
  transfers: AllocationTransfer[];
  /** Σ des montants repris par la cible. */
  movedTotal: number;
}

export interface PaymentCarryOverInput {
  /**
   * Affectations des documents sources. L'ordre décide qui est repris en
   * premier quand la capacité de la cible ne suffit pas — l'appelant le rend
   * déterministe (par date de paiement, par exemple).
   */
  sourceAllocations: CarryOverAllocation[];
  /** Net à payer du document cible. */
  targetDue: number;
  /** Ce que la cible porte DÉJÀ comme affectations (report partiel rejoué). */
  targetAlreadyAllocated?: number;
}

export function planPaymentCarryOver(input: PaymentCarryOverInput): PaymentCarryOverPlan {
  let capacity = round3(num(input.targetDue) - num(input.targetAlreadyAllocated));
  if (capacity <= PAYMENT_EPSILON) return { transfers: [], movedTotal: 0 };

  const transfers: AllocationTransfer[] = [];
  let movedTotal = 0;

  for (const allocation of input.sourceAllocations) {
    if (capacity <= PAYMENT_EPSILON) break;

    // Une affectation négative (remboursement imputé) ou nulle ne se reprend
    // pas : elle appartient au document qui l'a reçue.
    const available = round3(num(allocation.allocatedAmount));
    if (available <= PAYMENT_EPSILON) continue;

    const moved = round3(Math.min(available, capacity));
    if (moved <= PAYMENT_EPSILON) continue;

    const remainingOnSource = round3(available - moved);
    transfers.push({
      allocationId: allocation.id,
      paymentId: allocation.paymentId,
      sourceDocumentId: allocation.documentId,
      movedAmount: moved,
      // Un résidu d'un millime est un artefact d'arrondi, pas une créance :
      // la ligne part entière plutôt que de laisser une miette derrière elle.
      remainingOnSource: remainingOnSource <= PAYMENT_EPSILON ? 0 : remainingOnSource,
    });
    movedTotal = round3(movedTotal + moved);
    capacity = round3(capacity - moved);
  }

  return { transfers, movedTotal };
}
